import { CommonModule } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { LoadingSpinnerComponent } from "@evc/web-components";
import {
  DEFAULT_IMAGE_VIEWER_CONFIGS,
  DEFAULT_THREEJS_VIEWER_CONFIGS,
  FirstHoldAction,
  ImageViewerComponent,
  ImageViewerConfigs,
  MeasurementClientBase,
  MeasurementStateService,
  ThreejsViewerComponent,
  ThreejsViewerConfigs,
  ViewerError
} from "@evident/ngx-media-viewer";
import { TranslateModule } from "@ngx-translate/core";
import { InspectionImage } from "../../../_objects/inspection-image";
import { SpinnerService } from "../../../_services/spinner.service";
import { MeasurementPickerComponent } from "./measurement-picker/measurement-picker.component";
import { MeasurementResultsComponent } from "./measurement-results/measurement-results.component";
import { MeasurementService } from "./services/measurement.service";

/**
 * Desktop and IPLEX One measure with a mouse: a press that moves pans the image or rotates the model right
 * away, and a click (or a still hold) places a point. The viewer default, PLACE, is the touch-first borescope
 * behaviour: the first move of a press places and drags a point, and the model only rotates after a 250 ms
 * still hold.
 */
const MOUSE_FIRST_HOLD_ACTION = FirstHoldAction.PAN;

const MEASUREMENT_STYLES = {
  default: "green",
  highlighted: "blue",
  temporary: "red",
  reference: "orange"
};

@Component({
  selector: "app-measurement-merry",
  templateUrl: "./measurement-merry.component.html",
  styleUrl: "./measurement-merry.component.scss",
  standalone: true,
  imports: [
    CommonModule,
    ImageViewerComponent,
    LoadingSpinnerComponent,
    MeasurementPickerComponent,
    MeasurementResultsComponent,
    ThreejsViewerComponent,
    TranslateModule
  ]
})
export class MeasurementMerryComponent implements OnInit, OnDestroy {
  @Input({ required: true }) imageSrc!: string;
  @Input({ required: true }) inspectionImage!: InspectionImage;
  /** The image's stored measurement items, for the unit its measurements were last saved with. */
  @Input() measurementItems: unknown = null;

  @ViewChild("threeDViewer")
  public threeDViewer!: ThreejsViewerComponent;

  @ViewChild("imageViewer")
  public imageViewer!: ImageViewerComponent;

  public isLoading3DModel = signal(false);
  public error = signal<string | null>(null);
  public localImageSrc: string;

  public readonly imageViewerTopConfigs: ImageViewerConfigs = {
    ...DEFAULT_IMAGE_VIEWER_CONFIGS,
    autoInitialize: true,
    firstHoldAction: MOUSE_FIRST_HOLD_ACTION,
    maintainAspectRatio: true,
    maxZoom: 5,
    minZoom: 1,
    measurementStyles: MEASUREMENT_STYLES
  };

  public readonly threejsViewerConfig: ThreejsViewerConfigs = {
    ...DEFAULT_THREEJS_VIEWER_CONFIGS,
    autoInitialize: true,
    firstHoldAction: MOUSE_FIRST_HOLD_ACTION,
    fieldOfView: 70,
    nearClippingPane: 0.1,
    farClippingPane: 1000,
    defaultCameraZOffset: -20,
    defaultCameraMaxDistance: 2000,
    measurementStyles: MEASUREMENT_STYLES
  };

  constructor(
    private measurementClient: MeasurementClientBase,
    private measurementState: MeasurementStateService,
    private measurementService: MeasurementService,
    private spinnerService: SpinnerService
  ) {
    // The desktop app talks to the measurement service over Electron IPC; the client opens its mesh port once.
    if (!this.measurementClient.isInitialized) {
      this.measurementClient.initialize();
    }
    this.measurementState.setConfig({ requireDeleteConfirmation: false });
  }

  ngOnDestroy(): void {
    this.measurementState.reset();
  }

  async ngOnInit(): Promise<void> {
    this.measurementService.initializeUnitSystem(this.measurementItems);
    this.localImageSrc = await this.measurementService.getLocalImageUrl(this.imageSrc, this.inspectionImage);
  }

  async onImageLoaded() {
    await this.generate3DModel();
    // We need to wait for the model 3D to load before retrieving Legacy Measurements (no Measurements 3D in legacy)
    await this.measurementService.retrieveExistingMeasurements(this.localImageSrc);
  }

  public async generate3DModel() {
    this.isLoading3DModel.set(true);
    await this.threeDViewer.loadFromImageSrc(this.localImageSrc);
    this.isLoading3DModel.set(false);
  }

  public onViewerError(error: ViewerError | null) {
    if (error) {
      this.isLoading3DModel.set(false);
      this.error.set("measurement.cannotReconstruct3D");
      console.error("Viewer error:", error);
    } else {
      console.warn("Viewer error event fired with null/undefined error");
    }
  }

  public async save() {
    await this.measurementService.completeInProgressMeasurement();

    this.spinnerService.show();
    const base64ImagePreview = this.imageViewer.exportImageWithMeasurements("image/jpeg");
    await this.measurementService.saveMeasurements(this.localImageSrc, this.inspectionImage, base64ImagePreview);
    this.spinnerService.hide();
  }
}
